import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useReservations } from '../../hooks/useReservations';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import {
  Search,
  Calendar,
  Clock,
  User,
  Package,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Download,
  RefreshCw,
  BookmarkCheck,
  Ban,
  Hourglass,
  Printer,
  History,
  MapPin,
  Tag,
  Palette,
  Ruler,
  Mail,
  Phone,
  Home,
  ArrowRight,
  Filter,
  SortAsc,
  SortDesc,
  Truck,
  AlertTriangle,
  Warehouse,
  Store,
  PackageCheck,
  Plus,
  Pencil,
  Trash2,
  Check,
  X
} from 'lucide-react';
import { Reservation, ReservationStatus, InventoryItem } from '../../types';
import { supabase } from '../../lib/supabase';
import { useProductAttributes } from '../../hooks/use-product-attributes';
import { TransferReceiptModal } from '../inhouse/TransferReceiptModal';
import { sendB2BReadyNotification, sendB2BReservationConfirmation, type EmailAttachment } from '../../lib/b2bEmail';
import type { B2BBuyer } from '../../types/b2b';
import {
  generateProFormaInvoiceHTML,
  htmlToPdfBase64,
  buildEpcQrDataUrl,
  type SupplierProfile,
} from '../../lib/pdfGenerator';
import type { Invoice } from '../../types/invoice';
import { filterCcVsPrimary, parseEmailList } from '../../utils/emailRecipients';
import { getAdminId as resolveAdminId } from '../../utils/getAdminId';
import { getProductsBinLocations } from '../../lib/services/binInventoryService';
import { BinLocationDisplay } from '../inventory/BinLocationBadge';
import { formatBinPath } from '../../utils/binLocationFormat';
import type { ProductBinLocation } from '../../types/warehouse';

interface ProductInfo {
  id: string;
  name: string;
  sku?: string;
  reference_code?: string;
  category?: string;
  location?: string;
  color_id?: string;
  size_id?: string;
}

interface LocationInfo {
  id: string;
  name: string;
}

interface UserInfo {
  id: string;
  full_name: string;
  email: string;
}

interface ReservationSyncStatus {
  id: string;
  status: 'pending' | 'synced' | 'failed' | 'resolved';
  last_error: string | null;
  skipped_items: Array<{ productId: string; sku?: string | null; qty?: number; reason: string }>;
  attempts: number;
  resolved_at: string | null;
  updated_at: string;
}

type SortField = 'date' | 'customer' | 'amount' | 'status';
type SortDirection = 'asc' | 'desc';

export const ReservationsReport: React.FC = () => {
  const { user } = useAuth();
  const {
    reservations,
    loading,
    updateReservationStatus,
    fulfillReservation,
    cancelReservation,
    reactivateReservation,
    refreshReservations,
    confirmTransferReceipt,
    getPendingTransfers
  } = useReservations();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | 'all'>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [expandedReservation, setExpandedReservation] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showReactivateModal, setShowReactivateModal] = useState<string | null>(null);
  const [reactivateDate, setReactivateDate] = useState('');
  const [reactivateError, setReactivateError] = useState<string | null>(null);
  const [productInfo, setProductInfo] = useState<Record<string, ProductInfo>>({});
  const [binLocations, setBinLocations] = useState<Map<string, ProductBinLocation>>(new Map());
  const [locations, setLocations] = useState<Record<string, LocationInfo>>({});
  const [userNames, setUserNames] = useState<Record<string, UserInfo>>({});
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptProcessing, setReceiptProcessing] = useState(false);
  const [allProducts, setAllProducts] = useState<InventoryItem[]>([]);
  const [syncStatusByReservation, setSyncStatusByReservation] = useState<Record<string, ReservationSyncStatus>>({});
  const [syncIssuesModal, setSyncIssuesModal] = useState<string | null>(null);

  const [pdfCcEmails, setPdfCcEmails] = useState<string[]>([]);
  const [pdfCcOriginal, setPdfCcOriginal] = useState<string[]>([]);
  const [pdfCcSaving, setPdfCcSaving] = useState(false);
  const [pdfCcNotice, setPdfCcNotice] = useState<{ ok: boolean; msg: string } | null>(null);
  const [pdfCcExpanded, setPdfCcExpanded] = useState(false);
  const [pdfCcNewInput, setPdfCcNewInput] = useState('');
  const [pdfCcNewError, setPdfCcNewError] = useState<string | null>(null);
  const [pdfCcEditIndex, setPdfCcEditIndex] = useState<number | null>(null);
  const [pdfCcEditValue, setPdfCcEditValue] = useState('');
  const [pdfCcEditError, setPdfCcEditError] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const { colors, sizes } = useProductAttributes(getAdminId());

  useEffect(() => {
    const aid = getAdminId();
    if (!aid) return;
    let cancelled = false;
    void supabase
      .from('users')
      .select('reservation_pdf_cc_emails')
      .eq('id', aid)
      .single()
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const arr = (data?.reservation_pdf_cc_emails as string[] | undefined) ?? [];
        setPdfCcEmails(arr);
        setPdfCcOriginal(arr);
      });
    return () => { cancelled = true; };
  }, [getAdminId]);

  useEffect(() => {
    const fetchLocations = async () => {
      const adminId = getAdminId();
      if (!adminId) return;

      const { data } = await supabase
        .from('product_locations')
        .select('id, name')
        .eq('admin_id', adminId)
        .order('name');
      if (data) {
        const locMap: Record<string, LocationInfo> = {};
        data.forEach(loc => {
          locMap[loc.id] = { id: loc.id, name: loc.name };
        });
        setLocations(locMap);
      }
    };
    fetchLocations();
  }, [getAdminId]);

  useEffect(() => {
    const fetchProductInfo = async () => {
      const adminId = getAdminId();
      if (!adminId) return;

      // Skip when there is nothing to resolve yet.
      const hasItems = reservations.some(r => (r.reservation_items?.length ?? 0) > 0);
      if (!hasItems) return;

      // Tenant-scoped query (no per-id list) so it cannot exceed the request URL
      // length limit when many distinct products are reserved. Paginated with
      // .range() because PostgREST silently caps a single response at 1000 rows,
      // which would otherwise drop products for tenants with >1000 SKUs and render
      // their reserved items as "Unknown Product".
      const pageSize = 1000;
      const info: Record<string, ProductInfo> = {};
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data, error } = await supabase
          .from('products')
          .select('id, name, sku, reference_code, category, location, color_id, size_id')
          .eq('admin_id', adminId)
          .order('id', { ascending: true })
          .range(from, to);

        if (error) {
          console.error('Failed to load reservation product info:', error);
          return;
        }

        (data ?? []).forEach(product => {
          info[product.id] = {
            id: product.id,
            name: product.name,
            sku: product.sku,
            reference_code: product.reference_code,
            category: product.category,
            location: product.location,
            color_id: product.color_id,
            size_id: product.size_id
          };
        });

        hasMore = (data?.length ?? 0) === pageSize;
        page++;
      }

      setProductInfo(info);
    };
    fetchProductInfo();
  }, [reservations, getAdminId]);

  useEffect(() => {
    const fetchBinLocations = async () => {
      const adminId = getAdminId();
      if (!adminId) return;

      const productIds = Array.from(
        new Set(
          reservations.flatMap(r => (r.reservation_items ?? []).map(item => item.product_id))
        )
      );
      if (productIds.length === 0) return;

      setBinLocations(await getProductsBinLocations(productIds, adminId));
    };
    fetchBinLocations();
  }, [reservations, getAdminId]);

  useEffect(() => {
    const fetchProducts = async () => {
      const adminId = getAdminId();
      if (!adminId) return;

      // Paginated for the same reason as fetchProductInfo: PostgREST caps a single
      // response at 1000 rows, which would drop products for tenants with >1000 SKUs.
      const pageSize = 1000;
      const all: InventoryItem[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('admin_id', adminId)
          .order('id', { ascending: true })
          .range(from, to);

        if (error) {
          console.error('Failed to load products:', error);
          return;
        }

        if (data?.length) all.push(...(data as InventoryItem[]));
        hasMore = (data?.length ?? 0) === pageSize;
        page++;
      }

      setAllProducts(all);
    };
    fetchProducts();
  }, [getAdminId]);

  // Per-reservation Shopify sync status (durable outbox). Used to flag fulfilled
  // reservations whose Shopify push is still pending/failed or skipped some items,
  // so an operator can "control" them. Tenant-scoped + paginated (no per-id list)
  // to stay clear of the request URL-length limit and the 1000-row response cap.
  const fetchSyncStatus = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;
    if (reservations.length === 0) {
      setSyncStatusByReservation({});
      return;
    }

    const pageSize = 1000;
    const byReservation: Record<string, ReservationSyncStatus> = {};
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      const { data, error } = await supabase
        .from('shopify_sync_outbox')
        .select('id, reservation_id, status, last_error, skipped_items, attempts, resolved_at, updated_at')
        .eq('admin_id', adminId)
        .not('reservation_id', 'is', null)
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Failed to load reservation sync status:', error);
        return;
      }

      (data ?? []).forEach(row => {
        const rid = row.reservation_id as string | null;
        if (!rid || byReservation[rid]) return; // keep the most recent (desc order)
        byReservation[rid] = {
          id: row.id,
          status: row.status,
          last_error: row.last_error,
          skipped_items: Array.isArray(row.skipped_items) ? row.skipped_items : [],
          attempts: row.attempts,
          resolved_at: row.resolved_at,
          updated_at: row.updated_at,
        };
      });

      hasMore = (data?.length ?? 0) === pageSize;
      page++;
    }

    setSyncStatusByReservation(byReservation);
  }, [reservations, getAdminId]);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();
      reservations.forEach(r => {
        if (r.reserved_by) userIds.add(r.reserved_by);
        if (r.ready_marked_by) userIds.add(r.ready_marked_by);
        if (r.fulfilled_by) userIds.add(r.fulfilled_by);
        if (r.cancelled_by) userIds.add(r.cancelled_by);
      });

      if (userIds.size === 0) return;

      const { data } = await supabase
        .from('users')
        .select('id, full_name, email')
        .in('id', Array.from(userIds));

      if (data) {
        const names: Record<string, UserInfo> = {};
        data.forEach(u => {
          names[u.id] = { id: u.id, full_name: u.full_name, email: u.email };
        });
        setUserNames(names);
      }
    };
    fetchUserNames();
  }, [reservations]);

  const getUserDisplayName = (userId?: string) => {
    if (!userId) return '-';
    const userInfo = userNames[userId];
    return userInfo?.full_name || userInfo?.email || 'Unknown User';
  };

  const getColorName = (colorId?: string) => {
    if (!colorId) return '-';
    return colors.find(c => c.id === colorId)?.name || '-';
  };

  const getSizeName = (sizeId?: string) => {
    if (!sizeId) return '-';
    return sizes.find(s => s.id === sizeId)?.name || '-';
  };

  const getLocationName = (sourceLocationId?: string, fallbackLocation?: string) => {
    if (sourceLocationId && locations[sourceLocationId]) {
      return locations[sourceLocationId].name;
    }
    return fallbackLocation || '-';
  };

  const filteredAndSortedReservations = useMemo(() => {
    let filtered = reservations.filter(reservation => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          reservation.customer_name.toLowerCase().includes(query) ||
          reservation.reservation_number.toLowerCase().includes(query) ||
          reservation.customer_email?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      if (statusFilter !== 'all' && reservation.status !== statusFilter) {
        return false;
      }

      if (dateRange.start) {
        const startDate = new Date(dateRange.start);
        const reservationDate = new Date(reservation.reserved_at);
        if (reservationDate < startDate) return false;
      }

      if (dateRange.end) {
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        const reservationDate = new Date(reservation.reserved_at);
        if (reservationDate > endDate) return false;
      }

      return true;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.reserved_at).getTime() - new Date(b.reserved_at).getTime();
          break;
        case 'customer':
          comparison = a.customer_name.localeCompare(b.customer_name);
          break;
        case 'amount':
          comparison = a.total_amount - b.total_amount;
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [reservations, searchQuery, statusFilter, dateRange, sortField, sortDirection]);

  const stats = useMemo(() => {
    const reserved = reservations.filter(r => r.status === 'reserved').length;
    const ready = reservations.filter(r => r.status === 'ready').length;
    const fulfilled = reservations.filter(r => r.status === 'fulfilled').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;
    const expired = reservations.filter(r => r.status === 'expired').length;
    const pendingReceipt = reservations.filter(r => r.status === 'pending_receipt').length;
    const partialReceipt = reservations.filter(r => r.status === 'partial_receipt').length;
    const transferred = reservations.filter(r => r.status === 'transferred').length;
    const totalValue = reservations
      .filter(r => r.status === 'reserved' || r.status === 'ready')
      .reduce((sum, r) => sum + r.total_amount, 0);

    return { reserved, ready, fulfilled, cancelled, expired, pendingReceipt, partialReceipt, transferred, totalValue };
  }, [reservations]);

  // A fulfilled reservation "needs control" when its durable Shopify sync is still
  // pending/failed, or it succeeded but some items could not be pushed (skipped).
  // A manually-resolved row (operator clicked "I will fix it manually") is finished.
  const getSyncIssue = (reservationId: string): ReservationSyncStatus | null => {
    const s = syncStatusByReservation[reservationId];
    if (!s) return null;
    if (s.status === 'resolved' || s.resolved_at) return null;
    const hasProblem = s.status !== 'synced' || (s.skipped_items?.length ?? 0) > 0;
    return hasProblem ? s : null;
  };

  const needsControl = (reservation: Reservation): boolean =>
    reservation.status === 'fulfilled' && getSyncIssue(reservation.id) !== null;

  // Operator override: acknowledge a "Need control" reservation. Marks the outbox row
  // 'resolved' so the cron stops retrying and the warning clears. Does NOT touch stock.
  const handleResolveSyncManually = async (reservationId: string) => {
    const adminId = getAdminId();
    if (!adminId || !user) return;
    const { error } = await supabase
      .from('shopify_sync_outbox')
      .update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolved_by: user.id,
        updated_at: new Date().toISOString(),
      })
      .eq('admin_id', adminId)
      .eq('reservation_id', reservationId);
    if (error) {
      console.error('Failed to resolve sync manually:', error);
      return;
    }
    setSyncIssuesModal(null);
    await fetchSyncStatus();
  };

  const getStatusBadge = (status: ReservationStatus) => {
    const badges: Record<string, string> = {
      reserved: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      fulfilled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      pending_receipt: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      partial_receipt: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      transferred: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300'
    };
    return badges[status] || badges.reserved;
  };

  const getStatusIcon = (status: ReservationStatus) => {
    const icons: Record<string, React.ReactNode> = {
      reserved: <Clock className="h-4 w-4" />,
      ready: <BookmarkCheck className="h-4 w-4" />,
      fulfilled: <CheckCircle className="h-4 w-4" />,
      cancelled: <XCircle className="h-4 w-4" />,
      expired: <Hourglass className="h-4 w-4" />,
      pending_receipt: <Truck className="h-4 w-4" />,
      partial_receipt: <AlertTriangle className="h-4 w-4" />,
      transferred: <PackageCheck className="h-4 w-4" />
    };
    return icons[status] || icons.reserved;
  };

  const getStatusLabel = (status: ReservationStatus) => {
    const labels: Record<string, string> = {
      reserved: 'Reserved',
      ready: 'Ready',
      fulfilled: 'Fulfilled',
      cancelled: 'Cancelled',
      expired: 'Expired',
      pending_receipt: 'Pending Receipt',
      partial_receipt: 'Partial Receipt',
      transferred: 'Transferred'
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  const getExpirationInfo = (expiresAt: string, status: ReservationStatus) => {
    if (status === 'fulfilled' || status === 'cancelled' || status === 'expired') {
      return null;
    }

    const expires = new Date(expiresAt);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return { text: 'Expired', urgent: true };
    }
    if (diffDays === 1) {
      return { text: 'Expires tomorrow', urgent: true };
    }
    if (diffDays <= 3) {
      return { text: `Expires in ${diffDays} days`, urgent: true };
    }
    return { text: `Expires in ${diffDays} days`, urgent: false };
  };

  const handleMarkReady = async (reservationId: string) => {
    setProcessingId(reservationId);
    try {
      await updateReservationStatus(reservationId, 'ready');

      // Send ready notification email for B2B reservations
      const reservation = reservations.find(r => r.id === reservationId);
      if (reservation && (reservation as any).reservation_source === 'b2b' && (reservation as any).buyer_id) {
        try {
          const { data: buyer } = await supabase
            .from('b2b_buyers')
            .select('*')
            .eq('id', (reservation as any).buyer_id)
            .single();

          if (buyer) {
            const adminId = resolveAdminId(user);
            const { data: adminUser } = await supabase
              .from('users')
              .select('company_name, company_address, company_phone, company_email, vat_number')
              .eq('id', adminId)
              .single();

            await sendB2BReadyNotification(
              buyer as B2BBuyer,
              reservation,
              {
                company_name: adminUser?.company_name,
                company_address: adminUser?.company_address,
                company_phone: adminUser?.company_phone,
                company_email: adminUser?.company_email,
                vat_number: adminUser?.vat_number,
              }
            );
          }
        } catch (emailErr) {
          console.error('Failed to send B2B ready notification:', emailErr);
        }
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleFulfill = async (reservationId: string) => {
    setProcessingId(reservationId);
    try {
      const reservation = reservations.find(r => r.id === reservationId);
      const isB2B = (reservation as any)?.reservation_source === 'b2b';
      const res = await fulfillReservation(reservationId);

      if (!res.success) {
        alert(res.error || 'Failed to fulfill reservation.');
        return;
      }

      if (res.adjustments.length === 0) return;

      const lines = res.adjustments.map(a => {
        const name = a.productName || productInfo[a.productId]?.name || 'Item';
        if (a.reason === 'unavailable') {
          return `\u2022 ${name}: reserved ${a.requested}, none in stock \u2014 not fulfilled.`;
        }
        return `\u2022 ${name}: reserved ${a.requested}, only ${a.fulfilled} in stock \u2014 fulfilled ${a.fulfilled}.`;
      }).join('\n');

      const b2bNote = isB2B
        ? '\n\nNote: this is a B2B reservation. The pro-forma invoice already sent to the buyer is now out of sync with the fulfilled quantities. Please send a corrected invoice manually.'
        : '';

      alert(
        `Reservation fulfilled, but stock changed since it was created:\n\n${lines}\n\n` +
        'The reservation slip has been updated to reflect what was actually fulfilled.' +
        b2bNote
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleConfirmTransferReceipt = async (
    transferId: string,
    receivedItems: Array<{ item_id: string; product_id: string; received_quantity: number }>,
    receivingWorkerId: string
  ): Promise<boolean> => {
    setReceiptProcessing(true);
    try {
      const success = await confirmTransferReceipt(transferId, receivedItems, receivingWorkerId);
      if (success) {
        alert('Transfer received successfully! Inventory has been updated.');
        await refreshReservations();
      } else {
        alert('Failed to confirm transfer receipt. Please check inventory availability.');
      }
      return success;
    } catch (err) {
      alert('An error occurred while confirming transfer receipt.');
      return false;
    } finally {
      setReceiptProcessing(false);
    }
  };

  const fetchB2BContext = async (reservation: Reservation) => {
    const resAny = reservation as any;
    if (resAny.reservation_source !== 'b2b' || !resAny.buyer_id) return null;

    const adminId = getAdminId();
    if (!adminId) return null;

    const [buyerRes, supplierRes, invoiceRes] = await Promise.all([
      supabase.from('b2b_buyers').select('*').eq('id', resAny.buyer_id).single(),
      supabase.from('users').select('company_name, legal_entity_name, company_street, company_house_number, company_postal_code, company_city, company_country, company_address, company_phone, company_email, company_website, kvk_number, vat_number, company_tax_id, iban, bic, bank_name, logo_url, payment_terms_days').eq('id', adminId).single(),
      supabase.from('invoices').select('*, invoice_items(*)').eq('reservation_id', reservation.id).eq('is_pro_forma', true).maybeSingle(),
    ]);

    return {
      buyer: buyerRes.data as B2BBuyer | null,
      supplier: supplierRes.data as SupplierProfile | null,
      proFormaInvoice: invoiceRes.data as (Invoice & { invoice_items: any[]; admin_id?: string }) | null,
    };
  };

  const handleDownloadProForma = async (reservation: Reservation) => {
    setProcessingId(reservation.id);
    try {
      const ctx = await fetchB2BContext(reservation);
      if (!ctx?.buyer || !ctx?.supplier || !ctx?.proFormaInvoice) {
        alert('Pro-forma invoice not found for this reservation.');
        return;
      }

      const inv: Invoice = {
        ...ctx.proFormaInvoice,
        user_id: ctx.proFormaInvoice.admin_id,
        subtotal_amount: parseFloat(ctx.proFormaInvoice.subtotal_amount as any),
        tax_amount: parseFloat(ctx.proFormaInvoice.tax_amount as any),
        total_amount: parseFloat(ctx.proFormaInvoice.total_amount as any),
        invoice_items: (ctx.proFormaInvoice.invoice_items || []).map((it: any) => ({
          ...it, unit_price: parseFloat(it.unit_price), total_price: parseFloat(it.total_price),
        })),
      };

      const epcQr = await buildEpcQrDataUrl(ctx.supplier, inv.total_amount, inv.invoice_number);
      const html = generateProFormaInvoiceHTML(inv, ctx.buyer, ctx.supplier, epcQr);
      const pdfBase64 = await htmlToPdfBase64(html);

      const byteChars = atob(pdfBase64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ProForma-${inv.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download pro-forma:', err);
      alert('Failed to generate pro-forma PDF.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleResendConfirmation = async (reservation: Reservation) => {
    setProcessingId(reservation.id);
    try {
      const ctx = await fetchB2BContext(reservation);
      if (!ctx?.buyer || !ctx?.supplier) {
        alert('B2B context not found.');
        return;
      }

      const attachments: EmailAttachment[] = [];

      const items = (reservation.reservation_items || []).map(ri => {
        const pi = productInfo[ri.product_id];
        return {
          name: pi?.name || 'Product',
          sku: pi?.sku,
          quantity: ri.quantity,
          unit_price: ri.unit_price,
          total_price: ri.total_price,
        };
      });

      // Reservation table is rendered inline in the email body; skip the
      // reservation-PDF attachment to keep the SMTP payload small.

      if (ctx.proFormaInvoice) {
        try {
          const inv: Invoice = {
            ...ctx.proFormaInvoice,
            user_id: ctx.proFormaInvoice.admin_id,
            subtotal_amount: parseFloat(ctx.proFormaInvoice.subtotal_amount as any),
            tax_amount: parseFloat(ctx.proFormaInvoice.tax_amount as any),
            total_amount: parseFloat(ctx.proFormaInvoice.total_amount as any),
            invoice_items: (ctx.proFormaInvoice.invoice_items || []).map((it: any) => ({
              ...it, unit_price: parseFloat(it.unit_price), total_price: parseFloat(it.total_price),
            })),
          };
          const epcQr = await buildEpcQrDataUrl(ctx.supplier, inv.total_amount, inv.invoice_number);
          const invHtml = generateProFormaInvoiceHTML(inv, ctx.buyer, ctx.supplier, epcQr);
          const invPdf = await htmlToPdfBase64(invHtml, { scale: 1.5, jpegQuality: 0.85 });
          attachments.push({ filename: `ProForma-${inv.invoice_number}.pdf`, content: invPdf, contentType: 'application/pdf', encoding: 'base64' });
        } catch (e) { console.error('Pro-forma PDF failed:', e); }
      }

      const pickupDate = (reservation as any).pickup_scheduled_date || reservation.created_at;

      const { data: adminRow } = await supabase
        .from('users')
        .select('reservation_pdf_cc_emails')
        .eq('id', ctx.buyer.admin_id)
        .single();
      const rawCc = Array.isArray(adminRow?.reservation_pdf_cc_emails)
        ? (adminRow.reservation_pdf_cc_emails as string[]).filter(
            (e): e is string => typeof e === 'string' && e.trim().length > 0,
          )
        : [];
      const pdfCcAdmin = filterCcVsPrimary(rawCc, ctx.buyer.email);

      await sendB2BReservationConfirmation(
        ctx.buyer,
        reservation as any,
        items,
        { company_name: ctx.supplier.company_name, company_address: ctx.supplier.company_address, company_phone: ctx.supplier.company_phone, company_email: ctx.supplier.company_email, vat_number: ctx.supplier.vat_number },
        pickupDate,
        attachments.length > 0 ? attachments : undefined,
        reservation.id,
        pdfCcAdmin.length > 0 ? pdfCcAdmin : undefined,
      );

      alert('Confirmation email queued. The buyer will receive it shortly; check the row badge for delivery status.');
      // Refresh so the new email_sent_at / email_error becomes visible.
      setTimeout(() => { void refreshReservations(); }, 4000);
    } catch (err) {
      console.error('Failed to resend confirmation:', err);
      alert('Failed to queue confirmation email.');
    } finally {
      setProcessingId(null);
    }
  };

  const pdfCcIsDirty = useMemo(() => {
    if (pdfCcEmails.length !== pdfCcOriginal.length) return true;
    return pdfCcEmails.some((e, i) => e.toLowerCase() !== (pdfCcOriginal[i] ?? '').toLowerCase());
  }, [pdfCcEmails, pdfCcOriginal]);

  const addPdfCcEmail = () => {
    const parsed = parseEmailList(pdfCcNewInput);
    if (parsed.length === 0) {
      setPdfCcNewError('Enter a valid email address');
      return;
    }
    setPdfCcNotice(null);
    const existing = new Set(pdfCcEmails.map(e => e.toLowerCase()));
    const additions = parsed.filter(e => !existing.has(e.toLowerCase()));
    if (additions.length === 0) {
      setPdfCcNewError('Already in the list');
      return;
    }
    setPdfCcEmails([...pdfCcEmails, ...additions]);
    setPdfCcNewInput('');
    setPdfCcNewError(null);
  };

  const removePdfCcEmail = (index: number) => {
    setPdfCcNotice(null);
    setPdfCcEmails(pdfCcEmails.filter((_, i) => i !== index));
    if (pdfCcEditIndex === index) {
      setPdfCcEditIndex(null);
      setPdfCcEditValue('');
      setPdfCcEditError(null);
    }
  };

  const startEditPdfCc = (index: number) => {
    setPdfCcEditIndex(index);
    setPdfCcEditValue(pdfCcEmails[index] ?? '');
    setPdfCcEditError(null);
  };

  const cancelEditPdfCc = () => {
    setPdfCcEditIndex(null);
    setPdfCcEditValue('');
    setPdfCcEditError(null);
  };

  const commitEditPdfCc = () => {
    if (pdfCcEditIndex === null) return;
    const parsed = parseEmailList(pdfCcEditValue);
    if (parsed.length !== 1) {
      setPdfCcEditError('Enter a valid email address');
      return;
    }
    const next = parsed[0];
    const duplicateIdx = pdfCcEmails.findIndex(
      (e, i) => i !== pdfCcEditIndex && e.toLowerCase() === next.toLowerCase(),
    );
    if (duplicateIdx !== -1) {
      setPdfCcEditError('Already in the list');
      return;
    }
    setPdfCcNotice(null);
    setPdfCcEmails(pdfCcEmails.map((e, i) => (i === pdfCcEditIndex ? next : e)));
    setPdfCcEditIndex(null);
    setPdfCcEditValue('');
    setPdfCcEditError(null);
  };

  const saveReservationPdfCc = async () => {
    const aid = getAdminId();
    if (!aid) return;
    setPdfCcSaving(true);
    setPdfCcNotice(null);
    const list = pdfCcEmails;
    const { error } = await supabase
      .from('users')
      .update({ reservation_pdf_cc_emails: list })
      .eq('id', aid);
    setPdfCcSaving(false);
    if (error) {
      setPdfCcNotice({ ok: false, msg: error.message });
      return;
    }
    setPdfCcOriginal(list);
    setPdfCcNotice({
      ok: true,
      msg: 'Saved. Internal addresses will get a CC copy of B2B reservation emails (same invoice PDF as the buyer).',
    });
  };

  const handleReactivate = async () => {
    if (!showReactivateModal || !reactivateDate) return;
    const newExpiry = new Date(`${reactivateDate}T23:59:59`);
    if (Number.isNaN(newExpiry.getTime()) || newExpiry.getTime() <= Date.now()) {
      setReactivateError('Please pick a date in the future.');
      return;
    }
    setReactivateError(null);
    setProcessingId(showReactivateModal);
    try {
      const ok = await reactivateReservation(showReactivateModal, newExpiry.toISOString());
      if (ok) {
        setShowReactivateModal(null);
        setReactivateDate('');
      } else {
        setReactivateError('Failed to reactivate reservation. Please try again.');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancel = async () => {
    if (!showCancelModal) return;
    setProcessingId(showCancelModal);
    try {
      await cancelReservation(showCancelModal, cancelReason || undefined);
      setShowCancelModal(null);
      setCancelReason('');
    } finally {
      setProcessingId(null);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const headers = [
      'Reservation Number',
      'Customer Name',
      'Email',
      'Phone',
      'Status',
      'Item Name',
      'Item Reference',
      'Item Size',
      'Item Color',
      'Item Category',
      'Item Quantity',
      'Item Location',
      'Unit Price',
      'Line Total',
      'Subtotal',
      'Tax',
      'Total',
      'Reserved Date',
      'Reserved By',
      'Ready Date',
      'Ready Marked By',
      'Fulfilled Date',
      'Fulfilled By',
      'Cancelled Date',
      'Cancelled By',
      'Cancellation Reason',
      'Expires Date'
    ];

    const rows: string[][] = [];
    filteredAndSortedReservations.forEach(r => {
      const items = r.reservation_items || [];
      if (items.length === 0) {
        rows.push([
          r.reservation_number,
          r.customer_name,
          r.customer_email || '',
          r.customer_phone || '',
          r.status,
          '', '', '', '', '', '', '', '', '',
          r.subtotal.toFixed(2),
          r.tax_amount.toFixed(2),
          r.total_amount.toFixed(2),
          formatDateTime(r.reserved_at),
          getUserDisplayName(r.reserved_by),
          r.ready_at ? formatDateTime(r.ready_at) : '',
          getUserDisplayName(r.ready_marked_by),
          r.fulfilled_at ? formatDateTime(r.fulfilled_at) : '',
          getUserDisplayName(r.fulfilled_by),
          r.cancelled_at ? formatDateTime(r.cancelled_at) : '',
          getUserDisplayName(r.cancelled_by),
          r.cancellation_reason || '',
          new Date(r.expires_at).toLocaleDateString()
        ]);
      } else {
        items.forEach((item, idx) => {
          const product = productInfo[item.product_id];
          rows.push([
            idx === 0 ? r.reservation_number : '',
            idx === 0 ? r.customer_name : '',
            idx === 0 ? r.customer_email || '' : '',
            idx === 0 ? r.customer_phone || '' : '',
            idx === 0 ? r.status : '',
            product?.name || 'Unknown',
            product?.reference_code || '',
            getSizeName(product?.size_id),
            getColorName(product?.color_id),
            product?.category || '',
            item.quantity.toString(),
            getLocationName(item.source_location_id, product?.location),
            item.unit_price.toFixed(2),
            item.total_price.toFixed(2),
            idx === 0 ? r.subtotal.toFixed(2) : '',
            idx === 0 ? r.tax_amount.toFixed(2) : '',
            idx === 0 ? r.total_amount.toFixed(2) : '',
            idx === 0 ? formatDateTime(r.reserved_at) : '',
            idx === 0 ? getUserDisplayName(r.reserved_by) : '',
            idx === 0 && r.ready_at ? formatDateTime(r.ready_at) : '',
            idx === 0 ? getUserDisplayName(r.ready_marked_by) : '',
            idx === 0 && r.fulfilled_at ? formatDateTime(r.fulfilled_at) : '',
            idx === 0 ? getUserDisplayName(r.fulfilled_by) : '',
            idx === 0 && r.cancelled_at ? formatDateTime(r.cancelled_at) : '',
            idx === 0 ? getUserDisplayName(r.cancelled_by) : '',
            idx === 0 ? r.cancellation_reason || '' : '',
            idx === 0 ? new Date(r.expires_at).toLocaleDateString() : ''
          ]);
        });
      }
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reservations_detailed_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrintReservation = (reservation: Reservation) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const itemsHtml = reservation.reservation_items?.map(item => {
      const product = productInfo[item.product_id];
      const wasAdjusted = item.original_quantity != null && item.original_quantity !== item.quantity;
      const qtyBadge = wasAdjusted
        ? `<div style="font-size: 11px; color: #991b1b; margin-top: 2px; font-weight: 500;">originally ${item.original_quantity}${item.quantity === 0 ? ' \u2014 out of stock' : ''}</div>`
        : '';
      const rowStyle = item.quantity === 0
        ? 'background: #fef2f2;'
        : '';
      return `
        <tr style="${rowStyle}">
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${product?.name || 'Unknown'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #64748b;">${product?.sku || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #64748b;">${product?.reference_code || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${getSizeName(product?.size_id)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${getColorName(product?.color_id)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${product?.category || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center; font-weight: 600;">${item.quantity}${qtyBadge}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${getLocationName(item.source_location_id, product?.location)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; font-size: 12px; white-space: nowrap;">${formatBinPath(binLocations.get(item.product_id)) || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">\u20AC${item.unit_price.toFixed(2)}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 600;">\u20AC${item.total_price.toFixed(2)}</td>
        </tr>
      `;
    }).join('') || '<tr><td colspan="11" style="padding: 20px; text-align: center;">No items</td></tr>';

    const printAdjustments = (reservation.reservation_items || [])
      .filter(it => it.original_quantity != null && it.original_quantity !== it.quantity)
      .map(it => {
        const prod = productInfo[it.product_id];
        return {
          name: prod?.name || 'Unknown product',
          reference: prod?.reference_code,
          requested: it.original_quantity as number,
          fulfilled: it.quantity,
          unavailable: it.quantity === 0,
        };
      });

    const fulfillmentAdjustmentsHtml = printAdjustments.length > 0 ? `
      <div style="margin-top: 8px; padding: 10px 12px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 8px;">
        <div style="font-size: 12px; font-weight: 600; color: #92400e; margin-bottom: 6px;">
          Stock changed since reservation \u2014 quantities adjusted
        </div>
        <ul style="margin: 0; padding-left: 16px; color: #78350f; font-size: 12px; line-height: 1.5;">
          ${printAdjustments.map(a => `
            <li>
              <strong>${a.name}</strong>${a.reference ? ` (${a.reference})` : ''}:
              ${a.unavailable
                ? `reserved <strong>${a.requested}</strong>, none in stock \u2014 <span style="color: #991b1b; font-weight: 600;">not fulfilled</span>`
                : `reserved <strong>${a.requested}</strong>, fulfilled <strong>${a.fulfilled}</strong> (short by ${a.requested - a.fulfilled})`}
            </li>
          `).join('')}
        </ul>
      </div>
    ` : '';

    const statusHistoryHtml = `
      <div class="status-history">
        <div class="section-title">Status History</div>
        <div class="timeline">
          <div class="timeline-item">
            <div class="timeline-marker reserved"></div>
            <div class="timeline-content">
              <div class="timeline-label">Reserved</div>
              <div class="timeline-date">${formatDateTime(reservation.reserved_at)}</div>
              <div class="timeline-user">by ${getUserDisplayName(reservation.reserved_by)}</div>
            </div>
          </div>
          ${reservation.ready_at ? `
          <div class="timeline-item">
            <div class="timeline-marker ready"></div>
            <div class="timeline-content">
              <div class="timeline-label">Marked Ready</div>
              <div class="timeline-date">${formatDateTime(reservation.ready_at)}</div>
              <div class="timeline-user">by ${getUserDisplayName(reservation.ready_marked_by)}</div>
            </div>
          </div>
          ` : ''}
          ${reservation.fulfilled_at ? `
          <div class="timeline-item">
            <div class="timeline-marker fulfilled"></div>
            <div class="timeline-content">
              <div class="timeline-label">${printAdjustments.length > 0 ? 'Fulfilled (with adjustments)' : 'Fulfilled'}</div>
              <div class="timeline-date">${formatDateTime(reservation.fulfilled_at)}</div>
              <div class="timeline-user">by ${getUserDisplayName(reservation.fulfilled_by)}</div>
              ${fulfillmentAdjustmentsHtml}
            </div>
          </div>
          ` : ''}
          ${reservation.cancelled_at ? `
          <div class="timeline-item">
            <div class="timeline-marker cancelled"></div>
            <div class="timeline-content">
              <div class="timeline-label">Cancelled</div>
              <div class="timeline-date">${formatDateTime(reservation.cancelled_at)}</div>
              <div class="timeline-user">by ${getUserDisplayName(reservation.cancelled_by)}</div>
              ${reservation.cancellation_reason ? `<div class="timeline-reason">Reason: ${reservation.cancellation_reason}</div>` : ''}
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reservation - ${reservation.reservation_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1f2937; font-size: 14px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
          .title { font-size: 28px; font-weight: 700; color: #111827; }
          .subtitle { color: #6b7280; margin-top: 4px; font-size: 14px; }
          .badge { display: inline-block; padding: 8px 20px; border-radius: 9999px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
          .badge.reserved { background: #fef3c7; color: #92400e; }
          .badge.ready { background: #d1fae5; color: #065f46; }
          .badge.fulfilled { background: #dbeafe; color: #1e40af; }
          .badge.cancelled { background: #fee2e2; color: #991b1b; }
          .badge.expired { background: #f3f4f6; color: #374151; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 32px; }
          .info-card { background: #f9fafb; border-radius: 12px; padding: 16px; }
          .info-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; font-weight: 500; }
          .info-value { font-size: 15px; font-weight: 600; color: #111827; }
          .section-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 16px; margin-top: 24px; }
          .table-container { border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 14px 12px; text-align: left; font-size: 11px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
          th:nth-child(5), th:nth-child(7), th:nth-child(8) { text-align: right; }
          td { font-size: 13px; color: #374151; }
          .totals { max-width: 300px; margin-left: auto; background: #f9fafb; border-radius: 12px; padding: 16px; }
          .total-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
          .total-row.final { font-weight: 700; font-size: 18px; border-top: 2px solid #e5e7eb; padding-top: 12px; margin-top: 8px; color: #111827; }
          .status-history { margin-top: 32px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
          .timeline { display: flex; flex-direction: column; gap: 16px; margin-top: 16px; }
          .timeline-item { display: flex; gap: 16px; align-items: flex-start; }
          .timeline-marker { width: 12px; height: 12px; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }
          .timeline-marker.reserved { background: #f59e0b; }
          .timeline-marker.ready { background: #10b981; }
          .timeline-marker.fulfilled { background: #3b82f6; }
          .timeline-marker.cancelled { background: #ef4444; }
          .timeline-content { flex: 1; }
          .timeline-label { font-weight: 600; color: #111827; font-size: 14px; }
          .timeline-date { color: #6b7280; font-size: 13px; margin-top: 2px; }
          .timeline-user { color: #374151; font-size: 13px; margin-top: 2px; }
          .timeline-reason { color: #991b1b; font-size: 13px; margin-top: 4px; font-style: italic; }
          .notes-box { margin-top: 24px; padding: 16px; background: #fef3c7; border-radius: 12px; }
          .notes-title { font-weight: 600; color: #92400e; margin-bottom: 8px; }
          .notes-content { color: #78350f; }
          .footer { margin-top: 40px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          @media print { body { padding: 20px; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">Reservation Slip</div>
            <div class="subtitle">${reservation.reservation_number}</div>
          </div>
          <span class="badge ${reservation.status}">${reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}</span>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-label">Customer Name</div>
            <div class="info-value">${reservation.customer_name}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Reserved Date</div>
            <div class="info-value">${new Date(reservation.reserved_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          ${reservation.customer_phone ? `
          <div class="info-card">
            <div class="info-label">Phone</div>
            <div class="info-value">${reservation.customer_phone}</div>
          </div>
          ` : ''}
          ${reservation.customer_email ? `
          <div class="info-card">
            <div class="info-label">Email</div>
            <div class="info-value">${reservation.customer_email}</div>
          </div>
          ` : ''}
          <div class="info-card">
            <div class="info-label">Expires</div>
            <div class="info-value">${new Date(reservation.expires_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          ${reservation.customer_address ? `
          <div class="info-card">
            <div class="info-label">Address</div>
            <div class="info-value">${reservation.customer_address}</div>
          </div>
          ` : ''}
        </div>

        <div class="section-title">Reservation Items</div>
        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>SKU</th>
                <th>Reference</th>
                <th>Size</th>
                <th>Color</th>
                <th>Category</th>
                <th style="text-align: center;">Qty</th>
                <th>Location</th>
                <th>Bin</th>
                <th style="text-align: right;">Unit Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
        </div>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>\u20AC${reservation.subtotal.toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Tax</span>
            <span>\u20AC${reservation.tax_amount.toFixed(2)}</span>
          </div>
          <div class="total-row final">
            <span>Total</span>
            <span>\u20AC${reservation.total_amount.toFixed(2)}</span>
          </div>
        </div>

        ${statusHistoryHtml}

        ${reservation.notes ? `
        <div class="notes-box">
          <div class="notes-title">Notes</div>
          <div class="notes-content">${reservation.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Printed on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const StatusHistoryTimeline: React.FC<{ reservation: Reservation }> = ({ reservation }) => {
    const isTransfer = reservation.transfer_type === 'internal_transfer';

    const fulfillmentAdjustments = (reservation.reservation_items || [])
      .filter(item => item.original_quantity != null && item.original_quantity !== item.quantity)
      .map(item => {
        const product = productInfo[item.product_id];
        return {
          itemId: item.id,
          productId: item.product_id,
          name: product?.name || 'Unknown product',
          sku: product?.sku,
          reference: product?.reference_code,
          requested: item.original_quantity as number,
          fulfilled: item.quantity,
          reason: item.quantity === 0 ? ('unavailable' as const) : ('partial' as const),
        };
      });

    const timelineItems: Array<{
      status: string;
      label: string;
      date: string | null | undefined;
      user: string | null | undefined;
      show: boolean;
      colorClasses: string;
      reason?: string | null;
      adjustments?: typeof fulfillmentAdjustments;
    }> = [
      {
        status: 'reserved',
        label: isTransfer ? 'Transfer Created' : 'Reserved',
        date: reservation.reserved_at,
        user: reservation.reserved_by,
        show: true,
        colorClasses: isTransfer ? 'bg-indigo-500' : 'bg-amber-500'
      },
      {
        status: 'ready',
        label: 'Marked Ready',
        date: reservation.ready_at,
        user: reservation.ready_marked_by,
        show: !!reservation.ready_at && !isTransfer,
        colorClasses: 'bg-green-500'
      },
      {
        status: 'pending_receipt',
        label: 'Pending Receipt',
        date: null,
        user: null,
        show: isTransfer && (reservation.status === 'pending_receipt' || reservation.status === 'partial_receipt' || reservation.status === 'transferred'),
        colorClasses: 'bg-indigo-400'
      },
      {
        status: 'partial_receipt',
        label: 'Partially Received',
        date: reservation.status === 'partial_receipt' ? reservation.received_at : null,
        user: reservation.received_by,
        show: isTransfer && (reservation.status === 'partial_receipt'),
        colorClasses: 'bg-orange-500'
      },
      {
        status: 'transferred',
        label: 'Fully Received',
        date: reservation.status === 'transferred' ? reservation.received_at : null,
        user: reservation.received_by,
        show: isTransfer && reservation.status === 'transferred',
        colorClasses: 'bg-teal-500'
      },
      {
        status: 'fulfilled',
        label: fulfillmentAdjustments.length > 0 ? 'Fulfilled (with adjustments)' : 'Fulfilled',
        date: reservation.fulfilled_at,
        user: reservation.fulfilled_by,
        show: !!reservation.fulfilled_at && !isTransfer,
        colorClasses: fulfillmentAdjustments.length > 0 ? 'bg-amber-500' : 'bg-blue-500',
        adjustments: fulfillmentAdjustments.length > 0 ? fulfillmentAdjustments : undefined
      },
      {
        status: 'cancelled',
        label: 'Cancelled',
        date: reservation.cancelled_at,
        user: reservation.cancelled_by,
        show: !!reservation.cancelled_at,
        colorClasses: 'bg-red-500',
        reason: reservation.cancellation_reason
      },
      {
        status: 'expired',
        label: 'Expired',
        date: reservation.status === 'expired' ? reservation.updated_at : null,
        user: null,
        show: reservation.status === 'expired',
        colorClasses: 'bg-gray-500'
      }
    ].filter(item => item.show);

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Status History</h4>
        </div>
        <div className="relative">
          <div className="absolute left-[5px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700" />
          <div className="space-y-4">
            {timelineItems.map((item, idx) => (
              <div key={idx} className="relative flex items-start gap-4 pl-6">
                <div className={`absolute left-0 w-3 h-3 rounded-full ${item.colorClasses} ring-4 ring-white dark:ring-gray-800`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.label}</span>
                    {idx < timelineItems.length - 1 && (
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                  {item.date && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatDateTime(item.date)}
                    </p>
                  )}
                  {item.user && (
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {getUserDisplayName(item.user)}
                    </p>
                  )}
                  {item.reason && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 italic">
                      Reason: {item.reason}
                    </p>
                  )}
                  {item.adjustments && item.adjustments.length > 0 && (
                    <div className="mt-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                          Stock changed since reservation \u2014 quantities adjusted
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {item.adjustments.map(adj => (
                          <li
                            key={adj.itemId}
                            className="text-xs text-amber-900 dark:text-amber-200 flex items-start gap-1.5"
                          >
                            <span className={`mt-1 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              adj.reason === 'unavailable' ? 'bg-red-500' : 'bg-amber-500'
                            }`} />
                            <span className="min-w-0">
                              <span className="font-medium">{adj.name}</span>
                              {adj.reference && (
                                <span className="text-amber-700/70 dark:text-amber-300/60"> ({adj.reference})</span>
                              )}
                              {': '}
                              {adj.reason === 'unavailable' ? (
                                <>
                                  reserved <span className="font-semibold">{adj.requested}</span>, none in stock {'\u2014'}{' '}
                                  <span className="text-red-700 dark:text-red-400 font-semibold">not fulfilled</span>
                                </>
                              ) : (
                                <>
                                  reserved <span className="font-semibold">{adj.requested}</span>, fulfilled{' '}
                                  <span className="font-semibold">{adj.fulfilled}</span>{' '}
                                  <span className="text-amber-700 dark:text-amber-300/80">(short by {adj.requested - adj.fulfilled})</span>
                                </>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {getAdminId() && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setPdfCcExpanded(v => !v)}
            aria-expanded={pdfCcExpanded}
            className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
          >
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                Reservation email copies
                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium">
                  {pdfCcEmails.length}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {pdfCcEmails.length === 0
                  ? 'No internal recipients configured'
                  : pdfCcEmails.slice(0, 2).join(', ') + (pdfCcEmails.length > 2 ? `, +${pdfCcEmails.length - 2} more` : '')}
              </p>
            </div>
            {pdfCcIsDirty && !pdfCcExpanded && (
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Unsaved
              </span>
            )}
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform flex-shrink-0 ${pdfCcExpanded ? 'rotate-180' : ''}`}
            />
          </button>

          {pdfCcExpanded && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-4 sm:p-5 space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <strong className="text-gray-700 dark:text-gray-300">B2B portal:</strong> these addresses are CC'd on the buyer confirmation (invoice PDF attached).{' '}
                <strong className="text-gray-700 dark:text-gray-300">POS / in-house:</strong> each new reservation triggers a separate email with a reservation PDF. Duplicates matching the customer email are skipped.
              </p>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Add email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={pdfCcNewInput}
                    onChange={e => { setPdfCcNewInput(e.target.value); setPdfCcNewError(null); }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPdfCcEmail();
                      }
                    }}
                    placeholder="accounting@yourcompany.com"
                    className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={addPdfCcEmail}
                    disabled={!pdfCcNewInput.trim()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                {pdfCcNewError && (
                  <p className="text-xs text-red-600 dark:text-red-400">{pdfCcNewError}</p>
                )}
              </div>

              {pdfCcEmails.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
                  No addresses added yet.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {pdfCcEmails.map((email, index) => (
                    <li key={`${email}-${index}`} className="bg-gray-50 dark:bg-gray-900/40">
                      {pdfCcEditIndex === index ? (
                        <div className="p-2 sm:p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <input
                              type="email"
                              value={pdfCcEditValue}
                              autoFocus
                              onChange={e => { setPdfCcEditValue(e.target.value); setPdfCcEditError(null); }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); commitEditPdfCc(); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelEditPdfCc(); }
                              }}
                              className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                            <button
                              type="button"
                              onClick={commitEditPdfCc}
                              title="Save"
                              className="p-1.5 rounded-md text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditPdfCc}
                              title="Cancel"
                              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          {pdfCcEditError && (
                            <p className="text-xs text-red-600 dark:text-red-400">{pdfCcEditError}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2 p-2 sm:p-3 hover:bg-white dark:hover:bg-gray-800/60">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="text-sm text-gray-900 dark:text-gray-100 truncate font-mono">
                              {email}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => startEditPdfCc(index)}
                              title="Edit"
                              className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removePdfCcEmail(index)}
                              title="Delete"
                              className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {pdfCcNotice && (
                <p
                  className={`text-sm ${pdfCcNotice.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                >
                  {pdfCcNotice.msg}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveReservationPdfCc()}
                  disabled={pdfCcSaving || !pdfCcIsDirty}
                >
                  {pdfCcSaving ? 'Saving…' : 'Save addresses'}
                </Button>
                {pdfCcIsDirty && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setPdfCcEmails(pdfCcOriginal);
                      setPdfCcNewInput('');
                      setPdfCcNewError(null);
                      setPdfCcEditIndex(null);
                      setPdfCcEditValue('');
                      setPdfCcEditError(null);
                      setPdfCcNotice(null);
                    }}
                    disabled={pdfCcSaving}
                  >
                    Discard changes
                  </Button>
                )}
                {pdfCcIsDirty && (
                  <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                    Unsaved changes
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
            <Clock className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Reserved</span>
          </div>
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats.reserved}</p>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
            <BookmarkCheck className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Ready</span>
          </div>
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.ready}</p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-2">
            <CheckCircle className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Fulfilled</span>
          </div>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.fulfilled}</p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
            <XCircle className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Cancelled</span>
          </div>
          <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.cancelled}</p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Hourglass className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Expired</span>
          </div>
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{stats.expired}</p>
        </div>

        <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 border border-teal-200 dark:border-teal-800">
          <div className="flex items-center gap-2 text-teal-600 dark:text-teal-400 mb-2">
            <Package className="h-4 w-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Pending Value</span>
          </div>
          <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">
            {'\u20AC'}{stats.totalValue.toFixed(2)}
          </p>
        </div>
      </div>

      {(stats.pendingReceipt > 0 || stats.partialReceipt > 0 || stats.transferred > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-2">
              <Truck className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Pending Receipt</span>
            </div>
            <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{stats.pendingReceipt}</p>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Partial Receipt</span>
            </div>
            <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">{stats.partialReceipt}</p>
          </div>

          <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 border border-teal-200 dark:border-teal-800">
            <div className="flex items-center gap-2 text-teal-600 dark:text-teal-400 mb-2">
              <PackageCheck className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Transferred</span>
            </div>
            <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{stats.transferred}</p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by customer name, email, or reservation number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-shadow"
            />
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
              <Filter className="h-4 w-4" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ReservationStatus | 'all')}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="reserved">Reserved</option>
              <option value="ready">Ready</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
              <option value="pending_receipt">Pending Receipt</option>
              <option value="partial_receipt">Partial Receipt</option>
              <option value="transferred">Transferred</option>
            </select>

            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />

            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={refreshReservations}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            <Button
              variant="secondary"
              onClick={exportToCSV}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            Sort by:
          </span>
          {[
            { field: 'date' as SortField, label: 'Date' },
            { field: 'customer' as SortField, label: 'Customer' },
            { field: 'amount' as SortField, label: 'Amount' },
            { field: 'status' as SortField, label: 'Status' }
          ].map(({ field, label }) => (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1 transition-colors ${
                sortField === field
                  ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {label}
              {sortField === field && (
                sortDirection === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredAndSortedReservations.length}</span> of{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{reservations.length}</span> reservations
          </p>
        </div>

        {filteredAndSortedReservations.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            <Package className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No reservations found</p>
            <p className="text-sm mt-2 max-w-md mx-auto">
              {searchQuery || statusFilter !== 'all' || dateRange.start || dateRange.end
                ? 'Try adjusting your filters to see more results'
                : 'Reservations will appear here when created'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedReservations.map(reservation => {
              const isExpanded = expandedReservation === reservation.id;
              const expirationInfo = getExpirationInfo(reservation.expires_at, reservation.status);
              const isProcessing = processingId === reservation.id;

              return (
                <div key={reservation.id} className="bg-white dark:bg-gray-800">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    onClick={() => setExpandedReservation(isExpanded ? null : reservation.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {reservation.reservation_number}
                          </span>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(reservation.status)}`}>
                            {getStatusIcon(reservation.status)}
                            {getStatusLabel(reservation.status)}
                          </span>
                          {needsControl(reservation) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSyncIssuesModal(reservation.id);
                              }}
                              title="Shopify sync did not fully complete - click to review"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              Need control
                            </button>
                          )}
                          {reservation.transfer_type === 'internal_transfer' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                              <ArrowRight className="h-3 w-3" />
                              Internal Transfer
                            </span>
                          )}
                          {expirationInfo && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              expirationInfo.urgent
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {expirationInfo.text}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5" />
                            <span className="font-medium">{reservation.customer_name}</span>
                            {(reservation as any).reservation_source === 'b2b' && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 uppercase">B2B</span>
                            )}
                            {(reservation as any).reservation_source === 'b2b' && (() => {
                              if (reservation.email_sent_at) {
                                return (
                                  <span
                                    title={`Email sent ${formatDateTime(reservation.email_sent_at)}`}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                  >
                                    <CheckCircle className="h-3 w-3" /> Sent
                                  </span>
                                );
                              }
                              if (reservation.email_error) {
                                return (
                                  <span
                                    title={reservation.email_error}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                  >
                                    <XCircle className="h-3 w-3" /> Email failed
                                  </span>
                                );
                              }
                              return (
                                <span
                                  title="Confirmation email is being sent"
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                >
                                  <Clock className="h-3 w-3" /> Email pending
                                </span>
                              );
                            })()}
                          </div>
                          {reservation.transfer_type === 'internal_transfer' && reservation.source_location_id && reservation.destination_location_id && (
                            <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400">
                              <Warehouse className="h-3.5 w-3.5" />
                              <span>{locations[reservation.source_location_id]?.name || 'Source'}</span>
                              <ArrowRight className="h-3 w-3" />
                              <Store className="h-3.5 w-3.5" />
                              <span>{locations[reservation.destination_location_id]?.name || 'Destination'}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(reservation.reserved_at).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Package className="h-3.5 w-3.5" />
                            {reservation.reservation_items?.length || 0} items
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {'\u20AC'}{reservation.total_amount.toFixed(2)}
                          </p>
                        </div>
                        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 dark:bg-gray-900/50">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-4">
                        <div className="lg:col-span-2 space-y-6">
                          {reservation.transfer_type === 'internal_transfer' ? (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                                <ArrowRight className="h-4 w-4" />
                                Transfer Details
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                    <Warehouse className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">From</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {reservation.source_location_id ? locations[reservation.source_location_id]?.name || 'Unknown' : '-'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                    <Store className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">To</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {reservation.destination_location_id ? locations[reservation.destination_location_id]?.name || 'Unknown' : '-'}
                                    </p>
                                  </div>
                                </div>
                                {reservation.worker_name && (
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <User className="h-4 w-4 text-gray-500" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Worker</p>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{reservation.worker_name}</p>
                                    </div>
                                  </div>
                                )}
                                {reservation.received_at && (
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Received</p>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatDateTime(reservation.received_at)}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Customer Details
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Name</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{reservation.customer_name}</p>
                                  </div>
                                </div>
                                {reservation.customer_email && (
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <Mail className="h-4 w-4 text-gray-500" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</p>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{reservation.customer_email}</p>
                                    </div>
                                  </div>
                                )}
                                {reservation.customer_phone && (
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <Phone className="h-4 w-4 text-gray-500" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</p>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{reservation.customer_phone}</p>
                                    </div>
                                  </div>
                                )}
                                {reservation.customer_address && (
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                      <Home className="h-4 w-4 text-gray-500" />
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</p>
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{reservation.customer_address}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                Reservation Items
                                <span className="ml-auto text-xs font-normal text-gray-500 dark:text-gray-400">
                                  {reservation.reservation_items?.length || 0} items
                                </span>
                              </h4>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-900/50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                      <div className="flex items-center gap-1.5">
                                        <Tag className="h-3 w-3" />
                                        Name
                                      </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Reference</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                      <div className="flex items-center gap-1.5">
                                        <Ruler className="h-3 w-3" />
                                        Size
                                      </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                      <div className="flex items-center gap-1.5">
                                        <Palette className="h-3 w-3" />
                                        Color
                                      </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Category</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Qty</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                      <div className="flex items-center gap-1.5">
                                        <MapPin className="h-3 w-3" />
                                        Location
                                      </div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Bin</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Unit Price</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {reservation.reservation_items?.map((item, idx) => {
                                    const product = productInfo[item.product_id];
                                    return (
                                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium">
                                          {product?.name || 'Unknown Product'}
                                          {product?.sku && (
                                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                              SKU: {product.sku}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                          {product?.reference_code || '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            getSizeName(product?.size_id) !== '-'
                                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                              : 'text-gray-400'
                                          }`}>
                                            {getSizeName(product?.size_id)}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            getColorName(product?.color_id) !== '-'
                                              ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                                              : 'text-gray-400'
                                          }`}>
                                            {getColorName(product?.color_id)}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                          {product?.category || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          {(() => {
                                            const wasAdjusted = item.original_quantity != null && item.original_quantity !== item.quantity;
                                            const isUnavailable = wasAdjusted && item.quantity === 0;
                                            return (
                                              <div className="flex flex-col items-center gap-1">
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                                                  isUnavailable
                                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                                    : wasAdjusted
                                                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                                                }`}>
                                                  {item.quantity}
                                                </span>
                                                {wasAdjusted && (
                                                  <span
                                                    title={isUnavailable ? 'Out of stock at fulfillment' : 'Partially fulfilled due to stock change'}
                                                    className="text-[10px] font-medium text-red-600 dark:text-red-400 whitespace-nowrap"
                                                  >
                                                    {isUnavailable
                                                      ? `originally ${item.original_quantity} \u2014 out of stock`
                                                      : `originally ${item.original_quantity}`}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-3">
                                          {(() => {
                                            const locationName = getLocationName(item.source_location_id, product?.location);
                                            return (
                                              <span className={`inline-flex items-center gap-1 text-sm ${
                                                locationName !== '-'
                                                  ? 'text-gray-700 dark:text-gray-300'
                                                  : 'text-gray-400'
                                              }`}>
                                                {locationName !== '-' && <MapPin className="h-3 w-3" />}
                                                {locationName}
                                              </span>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-3">
                                          <BinLocationDisplay binLocations={binLocations} productId={item.product_id} />
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                                          {'\u20AC'}{item.unit_price.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                                          {'\u20AC'}{item.total_price.toFixed(2)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                              <div className="max-w-xs ml-auto space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                                  <span className="text-gray-900 dark:text-gray-100">{'\u20AC'}{reservation.subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500 dark:text-gray-400">Tax</span>
                                  <span className="text-gray-900 dark:text-gray-100">{'\u20AC'}{reservation.tax_amount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <span className="text-gray-900 dark:text-gray-100">Total</span>
                                  <span className="text-teal-600 dark:text-teal-400">{'\u20AC'}{reservation.total_amount.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <StatusHistoryTimeline reservation={reservation} />

                          {reservation.notes && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-4">
                              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">Notes</h4>
                              <p className="text-sm text-amber-700 dark:text-amber-400">{reservation.notes}</p>
                            </div>
                          )}

                          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Actions</h4>
                            <div className="flex flex-col gap-2">
                              {needsControl(reservation) && (
                                <Button
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSyncIssuesModal(reservation.id);
                                  }}
                                  className="w-full justify-center text-amber-700 hover:text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                >
                                  <AlertTriangle className="h-4 w-4 mr-2" />
                                  Review Shopify Sync
                                </Button>
                              )}

                              {reservation.status === 'expired' && (
                                <Button
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const d = new Date();
                                    d.setDate(d.getDate() + 7);
                                    setReactivateDate(d.toISOString().slice(0, 10));
                                    setReactivateError(null);
                                    setShowReactivateModal(reservation.id);
                                  }}
                                  disabled={isProcessing}
                                  loading={isProcessing}
                                  className="w-full justify-center"
                                >
                                  <Hourglass className="h-4 w-4 mr-2" />
                                  Reactivate Reservation
                                </Button>
                              )}

                              {reservation.transfer_type === 'internal_transfer' && (reservation.status === 'pending_receipt' || reservation.status === 'partial_receipt') && (
                                <Button
                                  variant="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowReceiptModal(true);
                                  }}
                                  disabled={isProcessing}
                                  className="w-full justify-center"
                                >
                                  <PackageCheck className="h-4 w-4 mr-2" />
                                  Confirm Receipt
                                </Button>
                              )}

                              {reservation.transfer_type !== 'internal_transfer' && reservation.status === 'reserved' && (
                                <Button
                                  variant="secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkReady(reservation.id);
                                  }}
                                  disabled={isProcessing}
                                  loading={isProcessing}
                                  className="w-full justify-center"
                                >
                                  <BookmarkCheck className="h-4 w-4 mr-2" />
                                  Mark as Ready
                                </Button>
                              )}

                              {reservation.transfer_type !== 'internal_transfer' && (reservation.status === 'reserved' || reservation.status === 'ready') && (
                                <>
                                  <Button
                                    variant="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFulfill(reservation.id);
                                    }}
                                    disabled={isProcessing}
                                    loading={isProcessing}
                                    className="w-full justify-center"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Fulfill Reservation
                                  </Button>

                                  <Button
                                    variant="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowCancelModal(reservation.id);
                                    }}
                                    disabled={isProcessing}
                                    className="w-full justify-center text-red-600 hover:text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    <Ban className="h-4 w-4 mr-2" />
                                    Cancel
                                  </Button>
                                </>
                              )}

                              {(reservation as any).reservation_source === 'b2b' && (
                                <>
                                  <Button
                                    variant="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadProForma(reservation);
                                    }}
                                    disabled={isProcessing}
                                    loading={isProcessing}
                                    className="w-full justify-center"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Pro-Forma PDF
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResendConfirmation(reservation);
                                    }}
                                    disabled={isProcessing}
                                    loading={isProcessing}
                                    className={`w-full justify-center ${reservation.email_error ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20' : ''}`}
                                  >
                                    <Mail className="h-4 w-4 mr-2" />
                                    {reservation.email_error
                                      ? 'Retry Confirmation Email'
                                      : reservation.email_sent_at
                                        ? 'Resend Confirmation Email'
                                        : 'Send Confirmation Email'}
                                  </Button>
                                </>
                              )}

                              <Button
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePrintReservation(reservation);
                                }}
                                className="w-full justify-center"
                              >
                                <Printer className="h-4 w-4 mr-2" />
                                Print Slip
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Ban className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Cancel Reservation
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to cancel this reservation? This action cannot be undone and will release the reserved items back to inventory.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4 resize-none"
              rows={3}
            />
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCancelModal(null);
                  setCancelReason('');
                }}
                className="flex-1"
              >
                Keep Reservation
              </Button>
              <Button
                variant="primary"
                onClick={handleCancel}
                loading={processingId === showCancelModal}
                className="flex-1 bg-red-600 hover:bg-red-700 focus:ring-red-500"
              >
                Cancel Reservation
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReactivateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-full">
                <Hourglass className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Reactivate Reservation
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Pick a new expiry date. The reservation will move back to{' '}
              <span className="font-semibold">Reserved</span> and stay active until the chosen date.
            </p>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              New expiry date
            </label>
            <input
              type="date"
              value={reactivateDate}
              min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
              onChange={(e) => {
                setReactivateDate(e.target.value);
                if (reactivateError) setReactivateError(null);
              }}
              className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-2"
            />
            {reactivateError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-2">{reactivateError}</p>
            )}
            <div className="flex gap-3 mt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowReactivateModal(null);
                  setReactivateDate('');
                  setReactivateError(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleReactivate}
                loading={processingId === showReactivateModal}
                disabled={!reactivateDate}
                className="flex-1"
              >
                Reactivate
              </Button>
            </div>
          </div>
        </div>
      )}

      {syncIssuesModal && (() => {
        const sync = syncStatusByReservation[syncIssuesModal];
        const res = reservations.find(r => r.id === syncIssuesModal);
        if (!sync) return null;
        const skipped = sync.skipped_items ?? [];
        const colorName = (id?: string) => colors.find(c => c.id === id)?.name || '';
        const sizeName = (id?: string) => sizes.find(s => s.id === id)?.name || '';
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    Shopify Sync - Needs Control
                  </h3>
                  {res && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {res.reservation_number} - {res.customer_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400">Sync status</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    sync.status === 'synced'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                    {sync.status === 'synced' ? 'Synced with skipped items' : sync.status === 'failed' ? 'Failed (will retry)' : 'Pending retry'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Attempts: {sync.attempts}</span>
                  <span>Updated {formatDateTime(sync.updated_at)}</span>
                </div>
                {sync.last_error && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400 break-words">
                    {sync.last_error}
                  </p>
                )}
              </div>

              {skipped.length > 0 ? (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Items not pushed to Shopify ({skipped.length})
                  </p>
                  <div className="space-y-2">
                    {skipped.map((it, idx) => {
                      const pi = productInfo[it.productId];
                      const sku = it.sku || pi?.sku || '';
                      const variant = [colorName(pi?.color_id), sizeName(pi?.size_id)].filter(Boolean).join(' / ');
                      return (
                        <div key={`${it.productId}-${idx}`} className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {pi?.name || 'Unknown Product'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {pi?.reference_code ? `${pi.reference_code}` : ''}{pi?.reference_code && variant ? ' - ' : ''}{variant}
                              </p>
                            </div>
                            {typeof it.qty === 'number' && it.qty > 0 && (
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                Qty {it.qty}
                              </span>
                            )}
                          </div>
                          {sku && (
                            <p className="mt-1 text-xs font-mono text-gray-500 dark:text-gray-400">SKU: {sku}</p>
                          )}
                          <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">{it.reason}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  The Shopify push has not completed yet. It retries automatically; check back shortly.
                  If it keeps failing, verify the product SKUs and location mappings in Shopify settings.
                </p>
              )}

              <div className="mt-5 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-3">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  If you correct these items directly in Shopify, mark this as handled to stop the
                  automatic retries and clear the warning. This does not change any stock.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                <Button variant="secondary" onClick={() => setSyncIssuesModal(null)}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleResolveSyncManually(syncIssuesModal)}
                  className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-500"
                >
                  <Check className="h-4 w-4 mr-2" />
                  I will fix it manually
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      <TransferReceiptModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        pendingTransfers={getPendingTransfers()}
        onConfirmReceipt={handleConfirmTransferReceipt}
        processing={receiptProcessing}
        products={allProducts}
        colors={colors}
        sizes={sizes}
        locations={Object.values(locations) as any}
      />
    </div>
  );
};
